// Price of a booking: one line per apartment, chosen service and fee, and
// the total. The booking card, the phone booking bar and the summary of the
// payment step all show this total, so they never disagree.

export const feeOf = (settings) => {
  const fee = Number(settings?.cleaning_fee);

  return settings?.enable_cleaning_fee && fee > 0 ? fee : 0;
};

// Cleaning fee lines of the booked rooms, like the server charges them: a
// room type with its own fee pays it per booked room, one line per room type,
// and the property fee is added once, without a name, when any booked room
// has no fee of its own.
export const cleaningFeeLines = (property, rooms = []) => {
  const propertyFee = feeOf(property?.iframe_settings);
  const booked = rooms || [];
  if (booked.length === 0) return propertyFee > 0 ? [{ amount: propertyFee }] : [];

  const roomTypes = property?.room_types || [];
  const lines = [];
  let withoutOwnFee = false;
  booked.forEach((room) => {
    const roomType = roomTypes.find((type) => String(type.id) === String(room.room_type_id));
    const ownFee = feeOf(roomType?.iframe_settings);
    if (ownFee <= 0) {
      withoutOwnFee = true;
      return;
    }
    const count = Number(room.count) || 1;
    const line = lines.find((item) => item.roomTypeId === String(room.room_type_id));
    if (line) {
      line.count += count;
      line.amount += ownFee * count;
    } else {
      lines.push({
        roomTypeId: String(room.room_type_id),
        name: roomType?.title ?? room.name ?? "",
        count,
        amount: ownFee * count,
      });
    }
  });

  const result = lines.map(({ roomTypeId, ...line }) => line);
  if (withoutOwnFee && propertyFee > 0) result.push({ amount: propertyFee });

  return result;
};

// Total cleaning fee of the booked rooms.
export const cleaningFeeOf = (property, rooms = []) =>
  cleaningFeeLines(property, rooms).reduce((sum, line) => sum + line.amount, 0);

// The price from the server, in cents, as the lines of the summary in the
// currency's main unit; `totalCents` is what the payment charges.
export const fromServerPrice = (price) => ({
  lines: (price?.lines || []).map((line) => ({ ...line, amount: line.amount / 100 })),
  total: (price?.total || 0) / 100,
  totalCents: price?.total || 0,
  currency: price?.currency || "eur",
});

const isPercentVoucher = (voucher) => Number(voucher?.type) === 2;

// Discount of a voucher: type 2 is a percentage of the apartments, any other
// type a fixed amount.
export const voucherDiscount = (voucher, roomTotal) => {
  if (!voucher) return 0;
  const value = Number(voucher.value) || 0;
  const discount = isPercentVoucher(voucher) ? (roomTotal * value) / 100 : value;

  return discount > 0 ? discount : 0;
};

export const bookingPrice = ({
  rooms = [],
  upsell = {},
  voucher = null,
  cityTax = 0,
  cleaningFee = 0,
  totalUpsell = () => 0,
} = {}) => {
  const booked = rooms || [];
  const lines = booked.map((room) => ({
    type: "room",
    name: room.name,
    guests: room.index,
    count: room.count,
    amount: room.price * room.count,
  }));
  const roomTotal = lines.reduce((sum, line) => sum + line.amount, 0);

  Object.entries(upsell || {}).forEach(([id, item]) => {
    const amount = totalUpsell(item);
    if (amount > 0) {
      lines.push({ type: "upsell", id, name: item.name, count: Number(item.value), amount });
    }
  });

  // Fees and the voucher belong to a stay, so only with an apartment chosen.
  if (booked.length > 0) {
    // One amount, or the lines of cleaningFeeLines().
    const fees = Array.isArray(cleaningFee) ? cleaningFee : [{ amount: Number(cleaningFee) || 0 }];
    fees
      .filter((fee) => fee.amount > 0)
      .forEach((fee) => lines.push({ type: "cleaning_fee", ...fee }));
    if (cityTax > 0) lines.push({ type: "city_tax", amount: cityTax });
    const discount = voucherDiscount(voucher, roomTotal);
    if (discount > 0) {
      lines.push({
        type: "voucher",
        percent: isPercentVoucher(voucher) ? Number(voucher.value) : null,
        amount: -discount,
      });
    }
  }

  return { lines, total: lines.reduce((sum, line) => sum + line.amount, 0) };
};

// Short breakdown under the total of the first step: the apartments together,
// each service, the cleaning fee together, the city tax and the voucher.
// Empty while the total is only the apartments, as it needs no explanation.
export const priceBreakdown = (lines = []) => {
  const rows = [];
  // Rows with a name (services) stay separate, the others add up.
  const add = (type, amount, extra = {}) => {
    const row = extra.name ? null : rows.find((item) => item.type === type);
    if (row) {
      row.amount += amount;
    } else {
      rows.push({ type, amount, ...extra });
    }
  };
  (lines || []).forEach((line) => {
    if (line.type === "room") add("accommodation", line.amount);
    else if (line.type === "upsell") add("upsell", line.amount, { name: line.name });
    else if (line.type === "voucher") add("voucher", line.amount, { percent: line.percent ?? null });
    else add(line.type, line.amount);
  });

  return rows.some((row) => row.type !== "accommodation") ? rows : [];
};

// Price on a property card. With the stay chosen it is the stay price, plus
// the property cleaning fee of a single apartment; a "from" price of several
// apartments leaves the fee out, as each apartment may have its own. Without
// dates it is the lowest nightly rate, never with a fee for the whole stay.
export const listingPrice = (property, withStay) => {
  if (withStay && property?.totalPrice > 0) {
    const isFrom = Boolean(property.isFromPrice);

    return {
      amount: property.totalPrice + (isFrom ? 0 : feeOf(property.iframe_settings)),
      isFrom,
      perNight: false,
    };
  }
  if (property?.basePrice > 0) {
    return { amount: property.basePrice, isFrom: true, perNight: true };
  }

  return null;
};
